import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Link as LinkIcon, Copy } from "lucide-react";

/**
 * "Text borrower" modal — mints a magic-link upload URL, drops it into an
 * SMS body, and sends via the shared Twilio endpoint. Loan reps + admins.
 */
export default function TextBorrowerModal({ open, onClose, app }) {
  const [phone, setPhone] = useState("");
  const [body, setBody] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !app?.id) return;
    setPhone(app.borrower?.phone || "");
    setLinkUrl("");
    setBody("");
    setBusy(true);
    api
      .post(`/applications/${app.id}/upload-link`)
      .then((r) => {
        setLinkUrl(r.data.url);
        const biz = app.business_legal_name || app.borrower?.name || "your file";
        setBody(
          `Hi from ${app.loan_rep?.name || "Venture Nationals"} — please upload your documents for ${biz} here: ${r.data.url}`,
        );
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  }, [open, app]);

  const send = async () => {
    if (!phone.trim()) { toast.error("Phone number required"); return; }
    if (!body.trim()) { toast.error("Message body required"); return; }
    setSending(true);
    try {
      await api.post("/admin/send-sms", {
        to: phone.trim(),
        body: body.trim(),
        application_id: app.id,
      });
      toast.success("Text sent");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="text-borrower-modal">
        <DialogHeader>
          <DialogTitle>Text borrower</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="small-caps text-slate-500 mb-1">Send to</div>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 555 555 0100"
              data-testid="text-borrower-phone"
              className="font-semibold"
            />
          </div>

          {busy ? (
            <div className="small-caps text-slate-400">Generating upload link…</div>
          ) : linkUrl ? (
            <div>
              <div className="small-caps text-slate-500 mb-1 flex items-center justify-between">
                <span>Upload link (14-day expiry)</span>
                <button
                  onClick={copyLink}
                  className="inline-flex items-center gap-1 small-caps text-[#1F2937] hover:opacity-70"
                  data-testid="text-borrower-copy-link"
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
              <a
                href={linkUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[13px] break-all text-[#1F2937] underline decoration-slate-300 hover:decoration-[#1F2937]"
                data-testid="text-borrower-link"
              >
                <LinkIcon className="inline h-3 w-3 mr-1" />
                {linkUrl}
              </a>
            </div>
          ) : null}

          <div>
            <div className="small-caps text-slate-500 mb-1">Message</div>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              data-testid="text-borrower-body"
              placeholder="Compose text…"
            />
            <div className="mt-1 small-caps text-slate-400 text-[10px]">
              {body.length} characters
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="small-caps text-slate-500 hover:text-[#1F2937]"
              data-testid="text-borrower-cancel"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={sending || busy || !linkUrl}
              className="inline-flex items-center gap-2 bg-[#1F2937] text-white px-4 py-2 small-caps disabled:opacity-40"
              data-testid="text-borrower-send"
            >
              <Send className="h-3.5 w-3.5" />
              {sending ? "Sending…" : "Send text"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
